type FeatureFlag<T extends boolean | number = boolean | number> = {
  description: string
  options: {
    label: string
    value: T
  }[]
}

const availableFeatureFlags = {
  "enable-view-transition": {
    description: "Enable View Transition APIs (requires refresh)",
    options: [
      { label: "Enabled", value: true },
      { label: "Disabled", value: false }
    ]
  },
  "logger-print-level": {
    description: "Set the log level for the logger instances.",
    options: [
      { label: "Verbose", value: 0 },
      { label: "Info", value: 1 },
      { label: "Warning", value: 2 },
      { label: "Error", value: 3 },
      { label: "Disabled", value: 4 }
    ]
  },
  "provisioning-force-version": {
    description:
      "Force the provisioning version to use when connecting to boards, regardless of the board's capabilities.",
    options: [
      { label: "Disabled", value: 0 },
      { label: "v1", value: 1 },
      { label: "v2", value: 2 }
    ]
  },
  "provisioning-force-webserial": {
    description: "Force the use of WebSerial protocol for serial board communication.",
    options: [
      { label: "Enabled", value: true },
      { label: "Disabled", value: false }
    ]
  },
  "provisioning-ignore-known-board-filter": {
    description:
      "Ignores the filter when connecting to known boards. This is useful for testing and debugging: use at your own risk.",
    options: [
      { label: "Enabled", value: true },
      { label: "Disabled", value: false }
    ]
  },
  "table-hover-preload": {
    description: "Enable route preloading on hover for internal links in the table component.",
    options: [
      { label: "Enabled", value: true },
      { label: "Disabled", value: false }
    ]
  }
} as const satisfies Record<string, FeatureFlag>

const defaultFeatureFlags: {
  [K in keyof typeof availableFeatureFlags]: (typeof availableFeatureFlags)[K]["options"][number]["value"]
} = {
  "enable-view-transition": false,
  "table-hover-preload": false,
  "provisioning-force-webserial": false,
  "provisioning-force-version": 0,
  "provisioning-ignore-known-board-filter": false,
  "logger-print-level": 3
}

export const featureFlags = Object.keys(availableFeatureFlags).reduce(
  (acc, key) => ({
    ...acc,
    [key]: {
      ...availableFeatureFlags[key as keyof typeof availableFeatureFlags],
      default: defaultFeatureFlags[key as keyof typeof defaultFeatureFlags]
    }
  }),
  {} as {
    [K in keyof typeof availableFeatureFlags]: (typeof availableFeatureFlags)[K] & {
      readonly default: (typeof defaultFeatureFlags)[K]
    }
  }
)
